import { Box, CircularProgress } from "@mui/material";
import React, { useContext } from "react";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ConfigurationContext from "../../context/Configuration";
import { BrandBadge, cardExpiry, cardTitle } from "./cardBrand";
import StripeCardForm from "./StripeCardForm";
import { BUTTON_RESET, C, FONT_BODY, FOCUS_RING, RADIUS, TYPE } from "../../utils/designTokens";
import { OptionCard, RadioGroup, metaSx, textButtonSx } from "./checkoutUi";

// Two card gateways, both offered. QorCommerce is the default and behaves
// exactly as it always has: saved cards, the payment sheet, charged inside
// placeOrder. Stripe is the alternative — its card form opens under its option,
// and Place Order places the order and then charges that card without leaving
// checkout.
export const CARD_PAYMENT_OPTION = {
  id: 3,
  payment: "QOR",
  label: "Credit / Debit Card",
};

export const STRIPE_PAYMENT_OPTION = {
  id: 4,
  payment: "STRIPE",
  label: "Credit / Debit Card (Stripe)",
};

const COD_PAYMENT_OPTION = {
  id: 2,
  payment: "COD",
  label: "Cash on Pickup",
};

const BRANDS = ["visa", "mastercard", "amex", "discover"];

function Brands() {
  return (
    <Box aria-hidden="true" sx={{ display: { xs: "none", sm: "flex" }, gap: 0.5, flexShrink: 0 }}>
      {BRANDS.map((b) => <BrandBadge key={b} brand={b} size="sm" />)}
    </Box>
  );
}

// What opens under the chosen option.
const panelSx = { mt: 1, p: 2, borderRadius: RADIUS.controlLg, bgcolor: C.surface };

/**
 * Step 3's ways to pay, as radio cards: the QorCommerce card (with the saved
 * card it will charge), the Stripe card form, and cash on a pickup order. A
 * gateway an admin turned off (Configuration → Card payment gateways) is not
 * offered.
 */
function PaymentCard({
  paymentMethod,
  setPaymentMethod,
  isPickUp,
  enablePickupCOD,
  // Saved QorCommerce cards + the one being paid with, owned by Checkout so the
  // sheet and this block always agree on the selection.
  savedCards = [],
  selectedCardToken,
  cardsLoading = false,
  onOpenPaymentSheet,
  // Receives Stripe's live instances so Place Order can charge the card typed
  // into the form, and the amount that form shows in wallets.
  stripeHandleRef,
  stripeAmount,
  // "Pay with", or "Pay the remaining $X with" once the wallet is on.
  heading = "Pay with",
}) {
  const configuration = useContext(ConfigurationContext);
  const qorOn = configuration?.qorPaymentsEnabled !== false;
  const stripeOn = configuration?.stripePaymentsEnabled !== false;
  const showCod = Boolean(isPickUp && enablePickupCOD);

  const isQorSelected = paymentMethod?.payment === "QOR";
  const isStripeSelected = paymentMethod?.payment === "STRIPE";
  const isCodSelected = paymentMethod?.payment === "COD";
  const selectedCard = savedCards.find((c) => c.token === selectedCardToken);
  const openSheet = () => {
    if (typeof onOpenPaymentSheet === "function") onOpenPaymentSheet();
  };

  const qorPanel = (
    <Box sx={panelSx}>
      {cardsLoading && !selectedCard ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
          <CircularProgress size={20} sx={{ color: C.muted }} />
        </Box>
      ) : selectedCard ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <BrandBadge brand={selectedCard.brand} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ ...TYPE.subHeading, color: C.ink }}>{cardTitle(selectedCard)}</Box>
            {selectedCard.expiry && <Box sx={metaSx}>{cardExpiry(selectedCard)}</Box>}
          </Box>
          <Box component="button" type="button" onClick={openSheet} aria-label="Change card" sx={textButtonSx}>
            Change
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Box sx={{ ...metaSx, flex: 1, minWidth: 180 }}>
            Add a card to pay. It's saved securely to your account for faster checkout next time.
          </Box>
          <Box
            component="button"
            type="button"
            onClick={openSheet}
            sx={{
              ...BUTTON_RESET,
              height: 44,
              px: 2,
              borderRadius: RADIUS.control,
              bgcolor: C.ink,
              color: "#FFFFFF",
              fontFamily: FONT_BODY,
              fontSize: 14,
              fontWeight: 700,
              "&:hover": { bgcolor: "#33312F" },
              ...FOCUS_RING,
            }}
          >
            Add a card
          </Box>
        </Box>
      )}
    </Box>
  );

  const stripePanel = stripeHandleRef ? (
    <Box sx={{ ...panelSx, bgcolor: C.bg, border: `1px solid ${C.border}` }}>
      <StripeCardForm amount={stripeAmount} handleRef={stripeHandleRef} />
    </Box>
  ) : null;

  const options = [
    qorOn && {
      key: "qor",
      checked: isQorSelected,
      select: () => setPaymentMethod(CARD_PAYMENT_OPTION),
      icon: <CreditCardRoundedIcon sx={{ fontSize: 26 }} />,
      title: "Card",
      caption: "Visa, Mastercard, Amex, Discover",
      trailing: <Brands />,
      panel: qorPanel,
    },
    stripeOn && {
      key: "stripe",
      checked: isStripeSelected,
      select: () => setPaymentMethod(STRIPE_PAYMENT_OPTION),
      icon: <CreditCardRoundedIcon sx={{ fontSize: 26 }} />,
      title: qorOn ? "Card via Stripe" : "Card",
      caption: qorOn ? "Enter a card with Stripe" : "Visa, Mastercard, Amex, Discover",
      trailing: qorOn ? null : <Brands />,
      // Mounted only while chosen, so Stripe.js loads for customers who pick it.
      panel: stripePanel,
    },
    showCod && {
      key: "cod",
      checked: isCodSelected,
      select: () => setPaymentMethod(COD_PAYMENT_OPTION),
      icon: <PaymentsOutlinedIcon sx={{ fontSize: 26 }} />,
      title: "Cash on pickup",
      caption: "Pay at the restaurant",
      trailing: null,
      panel: null,
    },
  ].filter(Boolean);

  return (
    <Box>
      <Box id="checkout-pay-with" sx={{ ...TYPE.subHeading, color: C.ink, mb: 1.25 }}>{heading}</Box>
      <RadioGroup labelledBy="checkout-pay-with" sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {options.map((o) => (
          <Box key={o.key}>
            <OptionCard
              checked={o.checked}
              onSelect={o.select}
              icon={o.icon}
              title={o.title}
              caption={o.caption}
              trailing={o.trailing}
            />
            {o.checked && o.panel}
          </Box>
        ))}
      </RadioGroup>
    </Box>
  );
}

export default PaymentCard;
